import type { ContributionDto } from '@signalvote/shared';
import { ResolutionOptionsDialog } from '../resolutions/ResolutionOptionsDialog';

/**
 * Thin wrapper around the workspace's multi-action resolution dialog
 * (refund / reattribute / redesignate). Why we wrap instead of using the
 * dialog directly at the dispatch site: the design-doc names this mount
 * `RefundDialog` and a future refund-only modal can slot in here without
 * churning every call site. Renaming the underlying component would
 * mislead readers — its surface includes reattribute and redesignate too.
 */
export interface RefundDialogProps {
  contribution: ContributionDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When true the dialog opens straight on the refund form rather than the
   * refund/reattribute/redesignate chooser. The ledger's row-level "Refund"
   * quick-action sets this — the treasurer asked to refund, so skip the menu.
   * The dispatcher (`?action=refund`) omits it and keeps the chooser, matching
   * the existing over-limit resolution entry point.
   */
  refundFirst?: boolean;
  /**
   * GitHub #61 — the amount a cure should propose, computed and clamped server-side on the
   * preflight warning whose Resolve affordance opened this. Forwarded straight through so the
   * refund / reattribute / redesignate forms all pre-fill with the excess instead of leaving a
   * treasurer to derive it against a 60-day deadline. Undefined when the dialog was reached
   * without one.
   */
  excessAmount?: number;
}

export function RefundDialog({
  contribution,
  open,
  onOpenChange,
  refundFirst = false,
  excessAmount,
}: RefundDialogProps) {
  return (
    <ResolutionOptionsDialog
      open={open}
      onOpenChange={onOpenChange}
      contribution={contribution}
      excessAmount={excessAmount}
      initialStep={refundFirst ? 'refund' : 'options'}
    />
  );
}
